import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { kc, api_url, environment } from "../config.js";
import axios from "axios";

export const initializeKeycloak = createAsyncThunk(
  "keycloak/initializeKeycloak",
  async (navigate, { rejectWithValue, dispatch }) => {
    try {
      if (kc.didInitialize) {
        return {
          keycloak: kc,
          token: kc.token,
          authenticated: kc.authenticated,
        };
      }
      const keycloakData = JSON.parse(localStorage.getItem("keycloak") || "{}");

      kc.onTokenExpired = () => dispatch(refreshToken());
      await kc.init({
        // onLoad: "check-sso",
        // silentCheckSsoRedirectUri:
        //   window.location.origin + "/silent-check-sso.html", // optional but recommended by Keycloak
        idToken: keycloakData.idToken,
        token: keycloakData.token,
        checkLoginIframe: false,
        enableLogging: true,
        refreshToken: keycloakData.refreshToken,
        scope: "openid profile",
      });
      if (!kc?.authenticated) {
        localStorage.removeItem("keycloak");
        localStorage.removeItem("userInfo");
        kc.clearToken();
        return { keycloak: kc, token: null, authenticated: false };
      } else {
        localStorage.setItem("keycloak", JSON.stringify(kc));
        dispatch(userAccountDetails(kc.token));
        return {
          keycloak: kc,
          token: kc.token,
          authenticated: true,
        };
      }
    } catch (error) {
      return rejectWithValue(error.message || "Error initializing Keycloak");
    }
  },
);

// Logout Action
export const logout = createAsyncThunk(
  "keycloak/logout",
  async (_, { rejectWithValue }) => {
    try {
      if (kc) {
        localStorage.removeItem("keycloak");
        localStorage.removeItem("userInfo");
        await kc.logout({ redirectUri: window.location.origin }); //+ "/silent-check-sso.html" ???
        return { authenticated: false };
      }
    } catch (error) {
      return rejectWithValue(error.message || "Error logging out");
    }
  },
);

// Refresh Token Action
export const refreshToken = createAsyncThunk(
  "keycloak/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const refreshed = await kc.updateToken(70);
      if (refreshed) {
        localStorage.setItem("keycloak", JSON.stringify(kc));
      }
      return {
        authenticated: true,
        token: kc.token,
        keycloak: kc,
      };
    } catch (error) {
      localStorage.removeItem("keycloak");
      localStorage.removeItem("userInfo");
      kc.clearToken();
      window.location.replace("/");
      return rejectWithValue(error.message || "Error refreshing token");
    }
  },
);

// Get User Account Info
export const userAccountDetails = createAsyncThunk(
  "keycloak/userAccountDetails",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${api_url}/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      localStorage.setItem("userInfo", JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Error fetching user account info",
      );
    }
  },
);

// // Get All Users' Info
// export const getAllUsers = createAsyncThunk(
//   'keycloak/getAllUsers',
//   async (_, { rejectWithValue }) => {
//     try {
//       // replace with axios request
//       const response = await axios.get('/api/users');
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.message || 'Error fetching users');
//     }
//   }
// );

// // Update User Role
// export const updateUserRole = createAsyncThunk(
//   'keycloak/updateUserRole',
//   async ({ userId, newRole }, { rejectWithValue }) => {
//     try {
//       // replace with axios request
//       const response = await axios.put(`/api/users/${userId}/role`, {
//         role: newRole,
//       });
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.message || 'Error updating user role');
//     }
//   }
// );

// Keycloak Slice
const keycloakSlice = createSlice({
  name: "user",
  initialState: {
    keycloak: null,
    token: null,
    authenticated: false,
    userInfo: null,
    allUsers: [],
    error: null,
    loading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initializeKeycloak.pending, (state) => {
        state.authenticated = false;
        state.error = null;
      })
      .addCase(initializeKeycloak.fulfilled, (state, action) => {
        state.keycloak = action.payload.keycloak;
        state.token = action.payload.token;
        state.authenticated = action.payload.authenticated;
        // state.userInfo = action.payload.userInfo;
        state.error = null;
      })
      .addCase(initializeKeycloak.rejected, (state, action) => {
        state.error = action.payload;
        state.authenticated = false;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.authenticated = false;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.keycloak = null;
        state.authenticated = false;
        state.userInfo = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload;
        state.authenticated = false;
      })

      // Refresh Token
      .addCase(refreshToken.pending, (state) => {
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.keycloak = action.payload.keycloak;
        state.authenticated = action.payload.authenticated;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.error = action.payload;
        // state.authenticated = false;
      })

      // Get User Account Info
      .addCase(userAccountDetails.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(userAccountDetails.fulfilled, (state, action) => {
        state.userInfo = action.payload;
        state.loading = false;
      })
      .addCase(userAccountDetails.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    // // Get All Users Info
    // .addCase(getAllUsers.pending, (state) => {
    //   state.error = null;
    // })
    // .addCase(getAllUsers.fulfilled, (state, action) => {
    //   state.allUsers = action.payload;
    // })
    // .addCase(getAllUsers.rejected, (state, action) => {
    //   state.error = action.payload;
    // })

    // // Update User Role
    // .addCase(updateUserRole.pending, (state) => {
    //   state.error = null;
    // })
    // .addCase(updateUserRole.fulfilled, (state, action) => {
    //   const updatedUser = action.payload;
    //   state.userInfo = state.userInfo.id === updatedUser.id ? updatedUser : state.userInfo;
    //   state.allUsers = state.allUsers.map(user =>
    //     user.id === updatedUser.id ? updatedUser : user
    //   );
    // })
    // .addCase(updateUserRole.rejected, (state, action) => {
    //   state.error = action.payload;
    // });
  },
});

export default keycloakSlice.reducer;
